import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogActions, Divider } from "@mui/material";
import {
	SBBox,
	SBTypography,
	SBIcon,
	SBIconButton,
	SBOutlinedButton,
} from "modules/Commons";
import { SBTranslate } from "i18n";
import { IConfigCSVErrors } from "domain/Config/Models/IConfigCSVErrors";
import { useTransferenceOrderRequester } from "modules/TransferenceOrder/hooks/useTransferenceOrderRequester";
import { useStyles } from "./style";
import TransferenceOrderUploaderHelperSelector from "./TransferenceOrderUploaderHelperSelector";
import TransferenceOrderUploaderHelperError from "./TransferenceOrderUploaderHelperError";
import TransferenceOrderLoadingState from "./TransferenceOrderLoadingState";

type UploadState = "onWaiting" | "onLoading" | "onSaving" | "onError";

interface TransferenceOrderUploaderHelperProps {
	open: boolean;
	handleClose: () => void;
	onSave?: () => void;
}

const TransferenceOrderUploaderHelper: React.FC<
	TransferenceOrderUploaderHelperProps
> = ({ open, handleClose: originalClose, onSave }) => {
	const classes = useStyles();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [currentState, setCurrentState] = useState<UploadState>("onWaiting");
	const [errors, setErrors] = useState<IConfigCSVErrors[]>([]);
	const [guid, setGuid] = useState<string>("");

	const requester = useTransferenceOrderRequester();

	const handleClose = () => {
		setErrors([]);
		setGuid("");
		setCurrentState("onWaiting");
		originalClose();
	};

	const handleFileAccepted = async (file: File) => {
		setCurrentState("onLoading");
		try {
			const result = await requester.importCSV(file);
			if (result.errors?.length) {
				setErrors(result.errors);
				setGuid(result.guid);
				setCurrentState("onError");
			} else {
				setCurrentState("onSaving");
				await requester.saveCSV(result.guid);
				onSave?.();
				handleClose();
			}
		} catch {
			setCurrentState("onWaiting");
		}
	};

	const handleIgnoreErrors = async () => {
		setCurrentState("onSaving");
		try {
			await requester.saveCSV(guid);
			onSave?.();
			handleClose();
		} catch {
			setCurrentState("onWaiting");
		}
	};

	const handleImportCSV = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			handleFileAccepted(file);
			event.target.value = "";
		}
	};

	const isLoading = currentState === "onLoading" || currentState === "onSaving";

	const triggerFileInput = () => {
		fileInputRef.current?.click();
	};

	const fetchState = (state: UploadState) => {
		switch (state) {
			case "onLoading":
				return <TransferenceOrderLoadingState text="waitForCSV" />;
			case "onSaving":
				return <TransferenceOrderLoadingState text="waitForCSVSaving" />;
			case "onError":
				return <TransferenceOrderUploaderHelperError errors={errors} />;
			default:
				return (
					<TransferenceOrderUploaderHelperSelector
						onFileAccepted={handleFileAccepted}
					/>
				);
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="md">
			<input
				type="file"
				accept=".csv"
				onChange={handleImportCSV}
				style={{ display: "none" }}
				ref={fileInputRef}
			/>
			<SBBox className={classes.configUploaderModal}>
				<SBBox className={classes.configUploaderSubModal}>
					<SBBox className={classes.configUploaderMenuHeader}>
						<SBTypography mr="auto" fontSize="1rem" fontWeight="700">
							{SBTranslate("uploadCSV")}
						</SBTypography>
						<SBIconButton onClick={handleClose}>
							<SBIcon icon="x" />
						</SBIconButton>
					</SBBox>
					<SBBox className={classes.uploaderContainer}>
						<SBBox className={classes.uploaderContent}>
							<DialogContent dividers>
								{fetchState(currentState)}
							</DialogContent>
							<Divider />
							<DialogActions>
								{currentState === "onError" && (
									<SBBox className={classes.reUploadButton}>
										<SBOutlinedButton
											onClick={handleIgnoreErrors}
											type="submit"
											color="error"
										>
											<SBIcon icon="checked" color="error" />
											<SBTypography
												ml={1}
												className={classes.buttonFont}
												style={{ textTransform: "capitalize" }}
											>
												{SBTranslate("IgnoreErros")}
											</SBTypography>
										</SBOutlinedButton>
									</SBBox>
								)}
								<SBBox className={classes.reUploadButton}>
									<SBOutlinedButton
										onClick={triggerFileInput}
										type="submit"
										disabled={isLoading}
									>
										<SBIcon icon="upload" color={isLoading ? "" : "primary"} />
										<SBTypography
											ml={1}
											className={classes.buttonFont}
											style={{ textTransform: "capitalize" }}
										>
											{SBTranslate("uploadCSV")}
										</SBTypography>
									</SBOutlinedButton>
								</SBBox>
							</DialogActions>
						</SBBox>
					</SBBox>
				</SBBox>
			</SBBox>
		</Dialog>
	);
};

export default TransferenceOrderUploaderHelper;
